import { AddressLinkDropdownPreview } from '@Components/AddressLinkDropdownPreview';
import { LinkedFieldWithLayoutSettings } from '@Components/LinkedFieldWithLayoutSettings';
import { addressColumns, contactColumns } from '@Constants/TableColumns';
import { type ApiAddress, type ApiContact, type ApiOrder, EnumFrontendLayoutSetting } from '@Model';
import { FrameEditAddress, FrameEditContact } from '@Modules/Stammdaten';
import {
    type AsyncStateUpdate,
    Button,
    FlexRow,
    Grid,
    GridColumns,
    type IGuiBinder,
    LabeledDiv,
    useLinkedField,
    useLinkProperty,
    useTranslate,
} from '@synqo/frontend-component-library';

interface IProps {
    binder: IGuiBinder<ApiOrder>;
    baseApiObjectPath: string;
    requestUpdateState: AsyncStateUpdate<ApiOrder>;
    canEdit: boolean;

    siteAddress: ApiOrder['site']['address'];
    customerKey: string | undefined;
    pickupSiteKey: string | undefined;
}

// @ai-generated 2026-10-22T10:53:17+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function OrderPartyFields({
    binder,
    baseApiObjectPath,
    requestUpdateState,
    canEdit,
    siteAddress,
    customerKey,
    pickupSiteKey,
}: IProps) {
    const i18n = useTranslate('');

    const linkSite = useLinkedField<ApiOrder, ApiAddress>(
        baseApiObjectPath,
        'site',
        FrameEditAddress,
        true,
        requestUpdateState,
    );
    const linkCustomer = useLinkedField<ApiOrder, ApiAddress>(
        baseApiObjectPath,
        'customer',
        FrameEditAddress,
        true,
        requestUpdateState,
    );
    const linkOrderer = useLinkedField<ApiOrder, ApiContact>(
        baseApiObjectPath,
        'orderer',
        FrameEditContact,
        true,
        requestUpdateState,
    );

    const siteField = useLinkProperty(binder, linkSite.linkApi);
    const customerField = useLinkProperty(binder, linkCustomer.linkApi);
    const ordererField = useLinkProperty(binder, linkOrderer.linkApi);

    const sameCustomerAndPickupSite = customerKey !== undefined && pickupSiteKey === customerKey;

    return (
        <Grid>
            <GridColumns spans={[4, 4, 4]}>
                <LinkedFieldWithLayoutSettings<ApiAddress>
                    layoutSettingsContext={EnumFrontendLayoutSetting.AddressesTableLayout}
                    {...siteField}
                    {...linkSite.events}
                    columns={addressColumns}
                    label={i18n('Lieferanschrift')}
                    value={siteAddress.display_string}
                    linked_key={siteAddress.foreign_key}
                    dropdownPreview={AddressLinkDropdownPreview}
                />
                {!sameCustomerAndPickupSite ? (
                    <LinkedFieldWithLayoutSettings<ApiAddress>
                        layoutSettingsContext={EnumFrontendLayoutSetting.AddressesTableLayout}
                        {...customerField}
                        {...linkCustomer.events}
                        columns={addressColumns}
                        label={i18n('Kunde')}
                        dropdownPreview={AddressLinkDropdownPreview}
                    />
                ) : (
                    <FlexRow justify="start">
                        <LabeledDiv label={'Kunde'} htmlFor={'ToggleShowClient'} id="customer-label">
                            <Button
                                label="ToggleShowClient"
                                labelId="customer-label"
                                onClick={() => requestUpdateState(linkCustomer.linkApi.unlink)}
                                appearance={'outlined'}
                                fullWidth
                                locked={!canEdit}
                            >
                                Abweichende Rechnungsadresse
                            </Button>
                        </LabeledDiv>
                    </FlexRow>
                )}
                <LinkedFieldWithLayoutSettings<ApiContact>
                    layoutSettingsContext={EnumFrontendLayoutSetting.ContactsTableLayout}
                    {...ordererField}
                    {...linkOrderer.events}
                    columns={contactColumns}
                    label={i18n('Anrufer')}
                />
            </GridColumns>
        </Grid>
    );
}
